import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import MainLayoutApple from '@/components/apple-ui/MainLayoutApple';
import { useTheme } from '@/contexts/ThemeContext';
import { Check, Zap, Star, Building2, Loader2, ArrowLeft, Wallet, Tag } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    id: 'starter', name: 'Starter', price: 999, words: 20000, icon: Zap, color: '#2563EB',
    features: ['20,000 words/month', 'Basic Translation', 'MTPE (Post-Editing)', 'Glossary & Translation Memory', 'Instruction Sets', 'Email Support (48h)'],
  },
  {
    id: 'professional', name: 'Professional', price: 2499, words: 60000, icon: Star, color: '#7C3AED', popular: true,
    features: ['60,000 words/month', 'Everything in Starter', 'CAT Tool', 'Proofreading', 'Super Review', 'Translation Quality Check', 'TQA PDF Reports', 'Priority Support (24h)'],
  },
  {
    id: 'business', name: 'Business', price: 7999, words: 200000, icon: Building2, color: '#059669',
    features: ['2,00,000 words/month', 'Everything in Professional', 'Team Accounts (3 members)', 'Bulk Document Upload', 'Custom Integrations', 'Dedicated Account Manager'],
  },
];

const WALLET_PACKS = [
  { id: 'pack_499', name: 'Starter Pack', price: 499, words: 3000, bonus: null },
  { id: 'pack_999', name: 'Basic Pack', price: 999, words: 7000, bonus: '+17% bonus' },
  { id: 'pack_2999', name: 'Pro Pack', price: 2999, words: 22000, bonus: '+47% bonus' },
  { id: 'pack_9999', name: 'Agency Pack', price: 9999, words: 80000, bonus: '+67% bonus' },
];

const loadCashfree = () => {
  if (window.Cashfree) return Promise.resolve();
  return new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    s.onload = resolve;
    document.body.appendChild(s);
  });
};

export default function PricingPageApple({ user, planInfo, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(null);
  const [currentPlan, setCurrentPlan] = useState('free');
  const [calcWords, setCalcWords] = useState('');
  const [suggestedPlan, setSuggestedPlan] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const cfMode = process.env.REACT_APP_CASHFREE_ENV === 'production' ? 'production' : 'sandbox';

  useEffect(() => { setCurrentPlan(planInfo?.plan || 'free'); }, [planInfo]);
  useEffect(() => {
    loadCashfree();
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) setPromoCode(codeFromUrl.toUpperCase());
  }, []);

  // Dark mode colors
  const bg = isDarkMode ? '#0f172a' : '#f8fafc';
  const cardBg = isDarkMode ? '#1e293b' : '#ffffff';
  const cardBorder = isDarkMode ? '#334155' : '#e5e7eb';
  const textPrimary = isDarkMode ? '#f1f5f9' : '#111827';
  const textSecondary = isDarkMode ? '#94a3b8' : '#6b7280';
  const textMuted = isDarkMode ? '#64748b' : '#9ca3af';
  const calcBg = isDarkMode ? '#1e3a5f' : '#eff6ff';
  const calcBorder = isDarkMode ? '#2563eb' : '#bfdbfe';
  const tableBg = isDarkMode ? '#1e293b' : '#ffffff';
  const tableRowAlt = isDarkMode ? '#0f172a' : '#f9fafb';
  const tableHeaderBg = isDarkMode ? '#0f172a' : '#f9fafb';
  const freeBg = isDarkMode ? '#1e293b' : '#f9fafb';
  const inputBg = isDarkMode ? '#0f172a' : '#ffffff';
  const inputBorder = isDarkMode ? '#334155' : '#93c5fd';

  const token = () => localStorage.getItem('token');

  const calculatePlan = (words) => {
    const n = parseInt(words) || 0;
    if (n === 0) { setSuggestedPlan(null); return; }
    if (n <= 500) setSuggestedPlan({ plan: 'free', label: 'Free Plan', reason: '500 words/month covers your usage', color: '#6B7280', price: '₹0' });
    else if (n <= 20000) setSuggestedPlan({ plan: 'starter', label: 'Starter', reason: '20,000 words/month covers your usage', color: '#2563EB', price: '₹999/month' });
    else if (n <= 60000) setSuggestedPlan({ plan: 'professional', label: 'Professional', reason: '60,000 words/month covers your usage', color: '#7C3AED', price: '₹2,499/month' });
    else if (n <= 200000) setSuggestedPlan({ plan: 'business', label: 'Business', reason: '2,00,000 words/month covers your usage', color: '#059669', price: '₹7,999/month' });
    else setSuggestedPlan({ plan: 'business', label: 'Business + Top-up', reason: 'Business plan + extra word packs for overflow', color: '#059669', price: '₹7,999/month + packs' });
  };

  const handlePromoRedeem = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/promo/redeem`,
        { code: promoCode.trim() },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      toast.success(res.data.message || 'Promo code applied!');
      setPromoCode('');
      setCurrentPlan(res.data.plan_id || 'professional');
      setTimeout(() => navigate('/billing'), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const openCheckout = async (payment_session_id) => {
    await loadCashfree();
    const cashfree = window.Cashfree({ mode: cfMode });
    return cashfree.checkout({ paymentSessionId: payment_session_id, redirectTarget: '_modal' });
  };

  const handleSubscribe = async (plan) => {
    if (currentPlan === plan.id) return toast.info('You are already on this plan');
    if (!token()) { navigate('/login?redirect=/pricing'); return; }
    setLoading(plan.id);
    try {
      const res = await fetch(`${API}/api/cashfree/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ plan_id: plan.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Order creation failed');
      const result = await openCheckout(data.payment_session_id);
      if (result?.error) { toast.error(result.error.message || 'Payment failed'); return; }
      if (result?.paymentDetails) {
        const vRes = await fetch(`${API}/api/cashfree/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
          body: JSON.stringify({ order_id: data.order_id }),
        });
        const vData = await vRes.json();
        if (vData.success) { toast.success(`🎉 ${plan.name} plan activated!`); setCurrentPlan(plan.id); setTimeout(() => navigate('/dashboard'), 2000); }
        else toast.error('Verification failed. Contact support@t09n.com');
      }
    } catch (err) { toast.error(err.message || 'Something went wrong'); }
    finally { setLoading(null); }
  };

  const handleWalletTopup = async (pack) => {
    if (!token()) { navigate('/login?redirect=/pricing'); return; }
    setLoading(pack.id);
    try {
      const res = await fetch(`${API}/api/cashfree/create-wallet-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ pack_id: pack.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Order creation failed');
      const result = await openCheckout(data.payment_session_id);
      if (result?.error) { toast.error(result.error.message || 'Payment failed'); return; }
      if (result?.paymentDetails) {
        const vRes = await fetch(`${API}/api/cashfree/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
          body: JSON.stringify({ order_id: data.order_id }),
        });
        const vData = await vRes.json();
        if (vData.success) { toast.success(`✅ ${pack.words.toLocaleString()} words added!`); setTimeout(() => navigate('/dashboard'), 2000); }
        else toast.error('Verification failed. Contact support@t09n.com');
      }
    } catch (err) { toast.error(err.message || 'Something went wrong'); }
    finally { setLoading(null); }
  };

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 20px', background: bg, minHeight: '100vh' }}>

        {/* Back */}
        <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, color: textSecondary, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 24, fontSize: 14 }}>
          <ArrowLeft size={16} /> Back
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: textPrimary, marginBottom: 8 }}>Choose Your Plan</h1>
          <p style={{ color: textSecondary, fontSize: 17 }}>Professional translation tools for Indian language translators</p>
          {currentPlan !== 'free' && (
            <span style={{ display: 'inline-block', marginTop: 10, padding: '4px 16px', background: isDarkMode ? '#064e3b' : '#ecfdf5', color: '#10b981', borderRadius: 20, fontSize: 13, fontWeight: 600, border: '1px solid #059669' }}>
              Current Plan: {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
            </span>
          )}
        </div>

        {/* Calculator */}
        <div style={{ background: calcBg, borderRadius: 16, border: `1px solid ${calcBorder}`, padding: 24, marginBottom: 32 }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#2563EB' }}>🧮 Not sure which plan? Calculate your needs</span>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', maxWidth: 480, margin: '0 auto' }}>
            <input
              type="number"
              placeholder="Words you translate per month..."
              value={calcWords}
              onChange={(e) => { setCalcWords(e.target.value); calculatePlan(e.target.value); }}
              style={{ flex: 1, padding: '12px 16px', borderRadius: 10, border: `1px solid ${inputBorder}`, fontSize: 14, outline: 'none', background: inputBg, color: textPrimary, boxSizing: 'border-box' }}
            />
            <button onClick={() => calculatePlan(calcWords)} style={{ padding: '12px 20px', background: 'linear-gradient(135deg, #2563EB, #7C3AED)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(37,99,235,0.4)' }}>
              Find Plan
            </button>
          </div>
          {suggestedPlan && calcWords && (
            <div style={{ marginTop: 16, background: cardBg, borderRadius: 12, border: `2px solid ${suggestedPlan.color}`, padding: '16px 20px', maxWidth: 480, margin: '16px auto 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 11, color: textMuted, marginBottom: 4, fontWeight: 600 }}>RECOMMENDED FOR YOU</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: suggestedPlan.color }}>{suggestedPlan.label}</div>
                <div style={{ fontSize: 13, color: textSecondary, marginTop: 2 }}>{suggestedPlan.reason}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: textPrimary }}>{suggestedPlan.price}</div>
                {suggestedPlan.plan !== 'free' && (
                  <button
                    onClick={() => { const p = PLANS.find(pl => pl.id === suggestedPlan.plan); if (p) handleSubscribe(p); else document.getElementById('wallet-packs').scrollIntoView({ behavior: 'smooth' }); }}
                    style={{ marginTop: 6, padding: '6px 14px', background: suggestedPlan.color, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}
                  >
                    Subscribe Now
                  </button>
                )}
              </div>
            </div>
          )}
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <span style={{ fontSize: 12, color: textMuted }}>💡 Typical: Legal doc (5K) • Product manual (20K) • Website (50K) • Large agency (500K+)</span>
          </div>
        </div>

        {/* Plan Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 16 }}>
          {PLANS.map((plan) => {
            const isCurrent = currentPlan === plan.id;
            const isLoading = loading === plan.id;
            const Icon = plan.icon;
            return (
              <div key={plan.id} style={{ background: cardBg, borderRadius: 16, border: `2px solid ${plan.popular ? plan.color : cardBorder}`, padding: 24, position: 'relative', boxShadow: plan.popular ? `0 4px 20px ${plan.color}25` : 'none' }}>
                {plan.popular && (
                  <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: plan.color, color: '#fff', padding: '3px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    ⭐ MOST POPULAR
                  </div>
                )}
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${plan.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <Icon size={22} color={plan.color} />
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: textPrimary, marginBottom: 4 }}>{plan.name}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 2 }}>
                  <span style={{ fontSize: 36, fontWeight: 800, color: textPrimary }}>₹{plan.price.toLocaleString()}</span>
                  <span style={{ color: textMuted, fontSize: 13 }}>/month</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: plan.color, marginBottom: 18 }}>{plan.words.toLocaleString()} words/month</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0' }}>
                  {plan.features.map((f, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: textSecondary, marginBottom: 8 }}>
                      <Check size={14} color={plan.color} style={{ flexShrink: 0, marginTop: 2 }} />{f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleSubscribe(plan)}
                  disabled={isCurrent || isLoading}
                  style={{ width: '100%', padding: '12px 0', borderRadius: 12, background: isCurrent ? (isDarkMode ? '#1e293b' : '#e5e7eb') : plan.id === 'starter' ? 'linear-gradient(135deg, #2563EB, #1d4ed8)' : plan.id === 'professional' ? 'linear-gradient(135deg, #7C3AED, #6d28d9)' : 'linear-gradient(135deg, #059669, #047857)', color: isCurrent ? textMuted : '#fff', border: `1px solid ${isCurrent ? cardBorder : plan.color}`, fontWeight: 700, fontSize: 14, cursor: isCurrent ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  {isLoading ? <><Loader2 size={15} className="animate-spin" /> Processing...</> : isCurrent ? '✓ Current Plan' : `Subscribe — ₹${plan.price.toLocaleString()}/mo`}
                </button>
              </div>
            );
          })}
        </div>

        {/* Free Plan */}
        <div style={{ background: freeBg, borderRadius: 12, border: `1px solid ${cardBorder}`, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 48 }}>
          <div>
            <div style={{ fontWeight: 700, color: textPrimary }}>Free Plan</div>
            <div style={{ color: textSecondary, fontSize: 13, marginTop: 2 }}>500 words/month — Basic translation only</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: textMuted }}>₹0</div>
            {currentPlan === 'free' && <div style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>Current Plan</div>}
          </div>
        </div>

        {/* Promo Code */}
        <div style={{ background: isDarkMode ? '#1e293b' : '#f0f4ff', borderRadius: 14, border: `1px solid ${isDarkMode ? '#334155' : '#c7d7fc'}`, padding: '20px 24px', marginBottom: 40, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <Tag size={18} color="#2563EB" />
            <span style={{ fontWeight: 700, fontSize: 15, color: textPrimary }}>Have a promo code?</span>
          </div>
          <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 240 }}>
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handlePromoRedeem()}
              placeholder="Enter code e.g. LLFREE-XXXXXX"
              style={{ flex: 1, padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${inputBorder}`, fontSize: 14, outline: 'none', background: inputBg, color: textPrimary, fontFamily: 'monospace', letterSpacing: 1 }}
            />
            <button
              onClick={handlePromoRedeem}
              disabled={promoLoading || !promoCode.trim()}
              style={{ padding: '10px 20px', background: promoCode.trim() ? 'linear-gradient(135deg, #2563EB, #7C3AED)' : (isDarkMode ? '#334155' : '#e5e7eb'), color: promoCode.trim() ? '#fff' : textMuted, border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: promoCode.trim() ? 'pointer' : 'default', whiteSpace: 'nowrap', transition: 'all 0.2s' }}
            >
              {promoLoading ? 'Applying…' : 'Apply'}
            </button>
          </div>
        </div>

        {/* PAYG */}
        <div id="wallet-packs" style={{ marginBottom: 48 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: isDarkMode ? '#1e3a5f' : '#eff6ff', padding: '6px 16px', borderRadius: 20, marginBottom: 12 }}>
              <Wallet size={16} color="#2563EB" />
              <span style={{ fontSize: 13, fontWeight: 600, color: '#2563EB' }}>PAY AS YOU GO</span>
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: textPrimary, marginBottom: 6 }}>Buy Word Packs</h2>
            <p style={{ color: textSecondary, fontSize: 15 }}>No monthly commitment. Words never expire. All features unlocked.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {WALLET_PACKS.map((pack) => {
              const isLoading = loading === pack.id;
              return (
                <div key={pack.id} style={{ background: cardBg, borderRadius: 14, border: `1px solid ${cardBorder}`, padding: 20, textAlign: 'center' }}>
                  {pack.bonus ? (
                    <div style={{ background: isDarkMode ? '#064e3b' : '#ecfdf5', color: '#059669', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20, display: 'inline-block', marginBottom: 10 }}>{pack.bonus}</div>
                  ) : <div style={{ height: 22, marginBottom: 10 }} />}
                  <div style={{ fontSize: 13, fontWeight: 600, color: textSecondary, marginBottom: 4 }}>{pack.name}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: textPrimary, marginBottom: 2 }}>₹{pack.price.toLocaleString()}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#2563EB', marginBottom: 16 }}>{pack.words.toLocaleString()} words</div>
                  <button
                    onClick={() => handleWalletTopup(pack)}
                    disabled={isLoading}
                    style={{ width: '100%', padding: '10px 0', borderRadius: 10, background: 'linear-gradient(135deg, #0ea5e9, #2563EB)', color: '#fff', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, boxShadow: '0 4px 12px rgba(14,165,233,0.35)' }}
                  >
                    {isLoading ? <><Loader2 size={14} className="animate-spin" /> Processing...</> : 'Buy Now'}
                  </button>
                </div>
              );
            })}
          </div>
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <span style={{ fontSize: 13, color: textMuted }}>✓ Words never expire &nbsp;&nbsp; ✓ All features unlocked &nbsp;&nbsp; ✓ No subscription needed</span>
          </div>
        </div>

        {/* Feature Table */}
        <div style={{ marginBottom: 40 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: textPrimary, marginBottom: 16, textAlign: 'center' }}>What's Included</h2>
          <div style={{ background: tableBg, borderRadius: 16, border: `1px solid ${cardBorder}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: tableHeaderBg, borderBottom: `1px solid ${cardBorder}` }}>
                  {['Feature', 'Free', 'Starter', 'Professional', 'Business'].map((h, i) => (
                    <th key={i} style={{ padding: '12px 16px', textAlign: i === 0 ? 'left' : 'center', fontWeight: 600, color: i === 0 ? textSecondary : ['#6B7280','#2563EB','#7C3AED','#059669'][i-1] }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['Words/month', '500', '20,000', '60,000', '2,00,000'],
                  ['Basic Translation', '✓', '✓', '✓', '✓'],
                  ['MTPE (Post-Editing)', '—', '✓', '✓', '✓'],
                  ['Glossary & TM', '—', '✓', '✓', '✓'],
                  ['CAT Tool', '—', '—', '✓', '✓'],
                  ['Proofreading', '—', '—', '✓', '✓'],
                  ['Translation Quality Check', '—', '—', '✓', '✓'],
                  ['TQA PDF Report', '—', '—', '✓', '✓'],
                  ['Super Review', '—', '—', '✓', '✓'],
                  ['Priority Support', '—', '—', '✓', '✓'],
                  ['Team Accounts', '—', '—', '—', '✓'],
                ].map(([f, ...vals], i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${cardBorder}`, background: i % 2 === 0 ? tableBg : tableRowAlt }}>
                    <td style={{ padding: '10px 16px', fontWeight: 500, color: textSecondary }}>{f}</td>
                    {vals.map((v, j) => (
                      <td key={j} style={{ padding: '10px 16px', textAlign: 'center', color: v === '✓' ? '#059669' : v === '—' ? (isDarkMode ? '#334155' : '#d1d5db') : textPrimary, fontWeight: v === '✓' ? 700 : 500 }}>{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ textAlign: 'center', fontSize: 13, color: textMuted }}>
          Questions? <a href="mailto:support@t09n.com" style={{ color: '#2563EB' }}>support@t09n.com</a>
        </div>
      </div>
    </MainLayoutApple>
  );
}
